import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { Box } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import {
  ILicenceTemplateApplicationProcessForm,
  ILicenceTemplateApplicationProcessPriceForm,
  ILicenceTemplateApplicationProcessSettingsForm,
} from 'models/licenceConfigurator';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { useLocation, useParams } from 'react-router-dom';
import {
  initialFormValuesApplicationProcessPrice,
  initialFormValuesApplicationProcessSettings,
} from '../helper';
import { useConfigurationLicence } from '../hooks';
import { FormLicenceTemplateApplicationProcessPage } from '../FormLicenceTemplateApplicationProcessPage';
import { EProcessTypes } from 'pages/LegalEntitiesPage/helpers';
import { LETypes } from 'constants/LegalEntityTypes';
import { isPathEqualToRouteName } from 'utils/route';

const LicenceTemplateEditApplicationProcessPage = () => {
  const { clearAlert, samePageAlert } = useAlert();
  const { t } = useTranslations();
  const { pathname }: any = useLocation();
  const { selectedLanguage } = useLang();
  const { processId = '', id = '' } = useParams<{ processId: string; id: string }>();
  const [formValues, setFormValues] = useState<
    | ILicenceTemplateApplicationProcessPriceForm
    | ILicenceTemplateApplicationProcessSettingsForm
  >();

  const isPriceEdit = isPathEqualToRouteName(
    pathname,
    ERouteLinks.EditLicenceTemplateApplicationProcessPricePage,
  );
  const isSettingsEdit = isPathEqualToRouteName(
    pathname,
    ERouteLinks.EditLicenceTemplateApplicationProcessSettingsPage,
  );

  const {
    getLicenceApplicationProccessDetail,
    licenceApplicationProccess,
    loadDictionaries,
    applicationProcessTypes,
  } = useConfigurationLicence();

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.applicationProcessTypes]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (!formValues && licenceApplicationProccess) {
      if (isSettingsEdit) {
        setFormValues({
          ...initialFormValuesApplicationProcessPrice,
          comment: licenceApplicationProccess.comment,
          legalEntityType: licenceApplicationProccess.legalEntityType as LETypes,
          processTypeUniqueId: licenceApplicationProccess.processTypeUniqueId as EProcessTypes,
        });
      } else if (isPriceEdit) {
        setFormValues({
          ...initialFormValuesApplicationProcessSettings,
          processTypeUniqueId: licenceApplicationProccess.processTypeUniqueId,
          priceName: licenceApplicationProccess.priceName,
          priceFee: licenceApplicationProccess.priceFee,
          priceDescription: licenceApplicationProccess.priceDescription,
        });
      }
    }
  }, [licenceApplicationProccess]);

  useEffect(() => {
    (async function init() {
      getLicenceApplicationProccessDetail(processId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('module.administration.name'),
            route: ERouteLinks.Administration,
          },
          {
            label: t('licenceConfiguration.header'),
            route: ERouteLinks.LicenceConfigurationPage,
          },
          {
            label: t('licenceConfiguration.detail.header'),
            route: ERouteLinks.LicenceTemplatePage.replace(':id', id),
          },
          {
            label: t('licence.applicationProcess.details.header'),
            route: ERouteLinks.LicenceTemplateApplicationProcessPage.replace(
              ':id',
              id,
            ).replace(':processId', processId),
          },
          t(
            isPriceEdit
              ? 'licenceConfiguration.price.edit.header'
              : 'licenceConfiguration.settings.edit.header',
          ),
        ]}
      />
      <StyledPageTitle variant="h4">
        {t(
          isPriceEdit
            ? 'licenceConfiguration.price.edit.header'
            : 'licenceConfiguration.settings.edit.header',
        )}
      </StyledPageTitle>

      {samePageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
      {formValues && (
        <FormLicenceTemplateApplicationProcessPage
          initialFormValues={formValues}
          version={licenceApplicationProccess?.version}
          isEdit={true}
        />
      )}
    </>
  );
};

export { LicenceTemplateEditApplicationProcessPage };
